import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { APP_URLS, locale, xTenantId } from '../../../../consts';
import { NavigationTemplate } from '../../../../layouts/NavigationTemplate';
import {
  UserBonusesQuery,
  useUserBonusesLazyQuery,
} from '../../../../libs/graphql/baseAppAPI/queries/__generated__/user-promo-collection.generated';
import { Endpoint } from '../../../../models/api.model';
import { useUserContext } from '../../../../providers/UserProvider';
import { Spinner } from '../../../shared/Spinner';
import {
  activePromoStatuses,
  availablePromoStatuses,
  ExistedPromos,
  FreeBetPromo,
  FreeSpinPromo,
  GigPromo,
  MagicXPromo,
  ParsedPromo,
  PromoTypes,
  RewardPromo,
  UserGigQuery,
} from '../types';
import { getImageUrl } from '../../../../utils/navigation';
import DOMPurify from 'dompurify';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { PromotionGames } from '../../../Games/games.model';
import { SEO } from '../../../seo/SEO';
import pandaMoneyImg from '../../../../assets/Games/PandaMoney.png';
import { Button } from '../../../shared/Button';
import { useAuth } from 'react-oidc-context';
import { RecommendedGameCarousel } from '../../../Games/RecommendedGames/RecommendedGameCarousel';
import { ProgressBar } from '../../../shared/ProgressBar';
import { CardCTASection } from '../PromotionCard/CardCTASection';
import { UserProfileBonusStatus } from '../../../../types.__generated__';
import { getDaysLeft } from '../../../../utils/dates';
import { MediaBreakpoint, useBreakpointInfo } from '../../../../hooks/useDeviceInfo';
import { mockWage, mockWageRequired } from '../consts';
import { isProgressBar, showCurrency } from '../utils';
import {
  UserFreeBetQuery,
  useUserFreeBetLazyQuery,
} from '../../../../libs/graphql/baseAppAPI/queries/__generated__/user-free-bet-get.generated';
import {
  UserFreeSpinQuery,
  useUserFreeSpinLazyQuery,
} from '../../../../libs/graphql/baseAppAPI/queries/__generated__/user-free-spin-get.generated';
import './styles';
import { getActiveMxPromotions, getAvailablegMxPromotions } from '../services';
import {
  MagicxDataGetBySlugQuery,
  useMagicxDataGetBySlugLazyQuery,
} from '../../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-magicx-data-by-slug.query.generated';
import { PromotionClaim } from '../PromotionClaimPrompt';
import { usePromoLoggedOut } from '../../../../hooks/usePromoLoggedOut';
import { PromotionError } from '../PromotionError/PromotionError';
import { useDispatch, useSelector } from 'react-redux';
import { loadPlatformPromotions } from '../../../../state/promotions';
import { setLoadPlatformPromotions } from '../../../../state/promotions/reducers';

export const PromotionMainBlock: FC = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfileId, user } = useUserContext();
  const isUserAuthenticated = useAuth()?.isAuthenticated;
  const sanitizer = DOMPurify.sanitize;
  const loadPlatformPromotionsState = useSelector(loadPlatformPromotions);
  const { pathname: pageUrl, hash } = location;

  const isTermsPage = hash === APP_URLS.terms;
  const promotionSlug = String(useParams().id);
  const promotionSubId = useParams().subId;
  const dispatch = useDispatch();

  const [directusMagicXPromotion, setDirectusMagicXPromotion] = useState<MagicxDataGetBySlugQuery | undefined>();
  const [resultPromotion, setResultPromotion] = useState<ParsedPromo | null>(null);
  const [fetchMagicXPromotionError, setFetchMagicXPromotionError] = useState<unknown>('');
  const [isPromoLoading, setIsPromoLoading] = useState<boolean>(true);
  const [showClaim, setShowClaim] = useState<boolean>(false);

  const isTablet = useBreakpointInfo([MediaBreakpoint.TABLET]);
  const isMobile = useBreakpointInfo([MediaBreakpoint.MOBILE]);

  const isActive = activePromoStatuses.includes(String(resultPromotion?.status));
  const isAvailable = availablePromoStatuses.includes(String(resultPromotion?.status));

  const promotion = directusMagicXPromotion?.magicx_promotions[0];
  const reward = directusMagicXPromotion?.magicx_rewards[0];
  const isDirectusPromo = promotion || reward;

  const {
    id,
    bonus_ID: bonusId,
    magicx_slug: magicXSlug,
    start_date: startDate,
    start_time: startTime,
    end_date: endDate,
    end_time: endTime,
    translations,
    eligible_games: eligibleGames,
    DM_Template_ID: magicDmBonusId,
    DM_Max_Amount: magicDmMaxAmount,
    bonus_type: promoType,
    thumbnail,
    thumb_expanded_desktop,
    thumb_expanded_mobile,
    reward_ID: rewardId,
    reward_type: rewardType,
  } = (promotion as typeof promotion & RewardPromo) || (reward as typeof promotion & RewardPromo) || {};

  const resTransl = translations?.[0];
  const title = resTransl?.title || '';
  const subtitle = resTransl?.subtitle || '';
  const description = resTransl?.description || '';
  const imgPath = getImageUrl(
    isMobile || isTablet ? thumb_expanded_mobile?.filename_disk : thumb_expanded_desktop?.filename_disk
  );
  const imgAlt = thumbnail?.title || '';
  const terms = resTransl?.terms;
  const howToClaim = resTransl?.how_to_claim;
  const eligibleCustomers = resTransl?.eligible_customers;
  const parsedPromo = {
    id,
    bonusId,
    rewardId,
    magicXSlug,
    startDate: startDate && startTime ? `${startDate}T${startTime}` : startDate || '',
    endDate: endDate && endTime ? `${endDate}T${endTime}` : endDate || '',
    title,
    description,
    imgPath,
    imgAlt,
    pageUrl,
    hideTerms: isTermsPage,
    dmMaxAmount: magicDmMaxAmount,
    dmBonusId: magicDmBonusId,
    promoType: promoType || rewardType,
    ctaCopy: translations?.[0]?.cta_copy,
    ctaUrl: translations?.[0]?.cta_url,
  };

  const STEPS_DATA = [
    t('promotions.details.steps.first'),
    t('promotions.details.steps.second'),
    t('promotions.details.steps.third'),
  ];
  const SUMMARY_DATA_TITLES = [
    t('promotions.details.summary.eligible-customers'),
    t('promotions.details.summary.how-to-claim'),
  ];

  const showCTA = isUserAuthenticated ? isActive || isAvailable : promoType !== PromoTypes.NETWORK;

  const fullPromo = { ...parsedPromo, ...resultPromotion };

  const showProgressBar = isProgressBar(resultPromotion?.status, parsedPromo.promoType);

  const { storedPromo } = usePromoLoggedOut(fullPromo, showClaim);

  const wageNumber = Number(resultPromotion?.wageredAmount ?? mockWage);
  const wageReqNumber = Number(resultPromotion?.wageringRequirementAmount ?? mockWageRequired);

  const findMXIndex = (promotions: MagicXPromo[]): MagicXPromo | undefined =>
    promotions.find((promo) => promo.slug === magicXSlug);

  const fetchMagicXPromotion = async (): Promise<void> => {
    const resultMXPromo: {
      wageredAmount?: string;
      wageringRequirementAmount?: string;
      status: UserProfileBonusStatus;
      magicXCta?: string;
      type?: string;
    } = { status: UserProfileBonusStatus.Available };

    try {
      const activeMXPromos = await getActiveMxPromotions(user?.email);
      const availableMXPromos = await getAvailablegMxPromotions(user?.email);

      const foundActiveMXPromo = findMXIndex(activeMXPromos);
      const foundAvailableMXPromo = findMXIndex(availableMXPromos);

      if (foundActiveMXPromo || foundAvailableMXPromo) {
        if (foundActiveMXPromo) {
          resultMXPromo.wageredAmount = foundActiveMXPromo?.achievementValue;
          resultMXPromo.wageringRequirementAmount = foundActiveMXPromo?.milestones?.[0].value;
        }

        const MXPromo = foundActiveMXPromo || foundAvailableMXPromo;

        resultMXPromo.status = foundActiveMXPromo ? UserProfileBonusStatus.Claimed : UserProfileBonusStatus.Available;
        resultMXPromo.magicXCta = MXPromo?.values?.gb?.cta?.[0]?.text;

        setResultPromotion(resultMXPromo);
      }
    } catch (error) {
      setFetchMagicXPromotionError(error);
    }
  };

  const handleGigPromos = (data: UserGigQuery): void => {
    const gigBonuses = (data as UserBonusesQuery).userBonuses?.data;
    const gigFreeBets = (data as UserFreeBetQuery).userFreeBet?.data;
    const gigFreeSpins = (data as UserFreeSpinQuery).userFreeSpin?.data;

    const resGigBonuses = gigBonuses || gigFreeBets || gigFreeSpins || [];

    const gigPromo = (resGigBonuses as GigPromo[])?.filter((gigBonus: GigPromo | FreeSpinPromo | FreeBetPromo) => {
      const resPromoId =
        gigBonus?.bonusTemplateId ||
        (gigBonus as FreeSpinPromo)?.freeSpinsTemplateId ||
        (gigBonus as FreeBetPromo)?.freeBetTemplateId;

      return (
        (parsedPromo.dmBonusId === gigBonus?.bonusTemplateId || parsedPromo.rewardId === resPromoId) &&
        activePromoStatuses.includes(String(gigBonus?.status))
      );
    });
    const resGiGPromo = gigPromo[Number(promotionSubId || Boolean(promotionSubId))];

    resGiGPromo &&
      setResultPromotion({
        status: resGiGPromo.status,
        wageredAmount: resGiGPromo.wageredAmount,
        wageringRequirementAmount: resGiGPromo.wageringRequirementAmount,
      });
  };

  const getPersistedPromoType = (): ExistedPromos => {
    const casedPromoType = parsedPromo.promoType?.toLowerCase();

    return {
      isDeposit: casedPromoType === PromoTypes.DEPOSIT_MATCH,
      isFreeBet: casedPromoType === PromoTypes.FREE_BET,
      isFreeSpins: casedPromoType === PromoTypes.FREE_SPINS,
      isWagerAndGet: casedPromoType === PromoTypes.WAGER_AND_GET,
    };
  };

  const [fetchMagicXDetailsBySlug, { error: fetchMagicXDetailsBySlugError }] = useMagicxDataGetBySlugLazyQuery({
    variables: {
      language: locale,
      slugs: [promotionSlug],
    },
    onCompleted: setDirectusMagicXPromotion,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: (e: ApolloError): void => {
      console.error(e.message);
      setIsPromoLoading(false);
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const gigFetchData = {
    variables: {
      xTenantId,
      userProfileId,
      pageLimit: 1000,
    },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
    notifyOnNetworkStatusChange: true,
    onCompleted: handleGigPromos,
    onError: (error: ApolloError) => console.error(error),
    context: { endpoint: Endpoint.appAPI },
  };

  const [fetchGigPromotion, { error: fetchGigPromotionError }] = useUserBonusesLazyQuery(gigFetchData);
  const [fetchFreeBetGigPromo, { error: fetchBonusesFreeBetGigError }] = useUserFreeBetLazyQuery(gigFetchData);
  const [fetchFreeSpinsGigPromo, { error: fetchBonusesFreeSpinsGigError }] = useUserFreeSpinLazyQuery(gigFetchData);

  const loadingError =
    fetchMagicXDetailsBySlugError ||
    fetchGigPromotionError ||
    fetchBonusesFreeBetGigError ||
    fetchBonusesFreeSpinsGigError ||
    fetchMagicXPromotionError;

  useEffect(() => {
    (async (): Promise<void> => {
      await fetchMagicXDetailsBySlug();
    })();
  }, []);

  useEffect(() => {
    (async (): Promise<void> => {
      if (isDirectusPromo) {
        if (isUserAuthenticated && loadPlatformPromotionsState) {
          const { isDeposit, isFreeBet, isFreeSpins, isWagerAndGet } = getPersistedPromoType();

          if (isDeposit) await fetchGigPromotion();

          if (isFreeBet) await fetchFreeBetGigPromo();

          if (isFreeSpins) await fetchFreeSpinsGigPromo();

          if (isWagerAndGet && user?.email) await fetchMagicXPromotion();

          dispatch(setLoadPlatformPromotions(false));
        }

        setIsPromoLoading(false);
      }
    })();
  }, [directusMagicXPromotion, user?.email, loadPlatformPromotionsState]);

  useEffect(() => {
    storedPromo && setShowClaim(true);
  }, [storedPromo]);

  const showError = Boolean(loadingError || !directusMagicXPromotion);

  return (
    <NavigationTemplate paddingDisabled>
      <SEO canonicalPath={pageUrl} />
      <div className="promotion-main-wrapper">
        {isPromoLoading ? (
          <Spinner />
        ) : showError ? (
          <PromotionError type={loadingError ? 'loading' : 'not-found'} />
        ) : (
          <>
            <div className="promotion-main-wrapper__back-button">
              <Button
                type="button"
                variant="secondary-alt"
                style={{ width: '8rem', height: '2.5rem' }}
                onClick={(): void => navigate(`${APP_URLS.promotionsRoot}`)}
              >
                {t('promotions.button.back.text')}
              </Button>
            </div>

            <div className="promotion-main-wrapper__content">
              <div
                className="promotion-main-wrapper__promo-image"
                style={{
                  backgroundImage: `url(${imgPath || pandaMoneyImg})`,
                }}
              />

              <div className="promotion-main-wrapper__details-section">
                <div className="promotion-main-wrapper__details-content">
                  <h1 className="promotion-main-wrapper__details-title">{title}</h1>
                  {subtitle && <h1 className="promotion-main-wrapper__details-title">{subtitle}</h1>}

                  {endDate && (
                    <div className="promotion-main-wrapper__status">
                      <span className="promotion-main-wrapper__time-left">
                        {getDaysLeft(endDate && endTime ? `${endDate}T${endTime}` : endDate)}
                      </span>
                    </div>
                  )}

                  <div className="promotion-main-wrapper__disclaimers">
                    <p dangerouslySetInnerHTML={{ __html: sanitizer(description) }} />
                  </div>
                  <div className="promotion-main-wrapper__steps">
                    <h3>{t('promotions.details.steps.title')}</h3>
                    <div className="promotion-main-wrapper__steps-progress">
                      {STEPS_DATA.map((step, index) => (
                        <React.Fragment key={step}>
                          <div className="promotion-main-wrapper__step">
                            <div className="promotion-main-wrapper__step-number">{++index}</div>
                            <span className="promotion-main-wrapper__step-text">{step}</span>
                          </div>
                          {index < STEPS_DATA.length && (
                            <svg
                              className="promotion-main-wrapper__step-dash"
                              viewBox="0 0 2 2"
                              preserveAspectRatio="none"
                              aria-hidden="true"
                            >
                              <line x1="0" y1="1" x2="100" y2="1" className="promotion-main-wrapper__step-dash-line" />
                            </svg>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>

                  {showProgressBar && (
                    <ProgressBar
                      showWageringInfo
                      showCurrency={showCurrency(resultPromotion?.type)}
                      value={wageNumber}
                      min={0}
                      max={wageReqNumber}
                      type="success"
                      className="promotion-main-wrapper__progress-bar"
                    />
                  )}
                  {showCTA && (
                    <CardCTASection
                      onClaimClick={(): void => setShowClaim(true)}
                      showSecondary={false}
                      isActivePromotions={isActive}
                      promo={parsedPromo}
                    />
                  )}

                  {isUserAuthenticated && (
                    <RecommendedGameCarousel
                      title={t('promotions.details.eligible-games.title')}
                      eligibleGames={eligibleGames as PromotionGames}
                      showArrows={true}
                    />
                  )}
                  {terms && (
                    <div className="promotion-main-wrapper__terms">
                      <h3>{t('promotions.details.terms.title')}</h3>
                      <div className="promotion-main-wrapper__terms-content">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: sanitizer(terms),
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="promotion-main-wrapper__summary">
                    {[eligibleCustomers, howToClaim].map(
                      (item, index) =>
                        item && (
                          <div key={index} className="promotion-main-wrapper__summary-item">
                            <h4 className="promotion-main-wrapper__summary-title">{SUMMARY_DATA_TITLES[index]}</h4>
                            <p
                              className="promotion-main-wrapper__summary-text"
                              dangerouslySetInnerHTML={{
                                __html: sanitizer(item),
                              }}
                            />
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {showClaim && <PromotionClaim promo={storedPromo || fullPromo} setShowClaim={setShowClaim} />}
      </div>
    </NavigationTemplate>
  );
};

export default PromotionMainBlock;
